/*
 * Copyright (c) 2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import { hex_md5 } from 'apmui/util/md5';

import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import { INPUT, IFormField } from 'apmui/components/form/Model';
import { DialogClass, DialogType, IDialogConfiguration } from 'apmui/components/dialog/Model';
import * as Dialog from 'apmui/components/dialog/DialogHelper';
import { CustomProtocolHandler, IClientConfiguration, PROGRESS_STATUS, getCustomProtocolLink, OS } from 'apmui/plugin';

import { userAgent } from 'apmui/util/UserAgent';

import deepCopy from 'apmui/util/deepCopy';
import { IResourceModel } from 'apmui/resource';
import ResourceType from 'apmui/resource/ResourceType';
import { PAGE, IPageModel } from 'apmui/page';

import 'apmui/components/dialog/Controller';
import 'apmui/components/form/Controller';

const INSTALLER_URL_FIELD = 'installerURL';
const LAUNCH_DETECTION_TIMEOUT = 4000;

const app = App.get();

enum NADialogAction {
    DOWNLOAD = 'download_action',
    RELOAD = 'reload_action',
    RESTART = 'restart_action',
    SHOW_DOWNLOAD_SCREEN = 'install_action',
    RESTART_NO_AUTOSTART_SUPPORTED = 'restart_noautostart_action',
}

function snaDebug(...args: any[]) {
    console.log(...args);
}

function launchNAResource(resource: IResourceModel, page: IPageModel) {
    if (
        page.pageType !== PAGE.WEBTOP ||
        (resource.type !== ResourceType.NETWORK_ACCESS && resource.type !== ResourceType.APP_TUNNEL)
    ) {
        return;
    }

    const na = page.webtop.network_access;
    const appt = resource.type === ResourceType.APP_TUNNEL;
    const { link, token } = snaGetLaunchLink(window.location.hostname, resource, na.components);

    let customProtocolHandler: CustomProtocolHandler | null = null;

    // TODO: close only dialogs related to current resources.
    const hideMessage = () => app.closeModal({ dialog: DialogClass.NETWORK_ACCESS, resolve: true });

    const onComplete = () => {
        hideMessage();
        app.dispatch(EventTags.RESOURCE_LAUNCH_COMPLETE, resource);
    };

    const onError = (httpError = 0, apmError = 0) => {
        snaDebug(`NA:onError:${token}: httpError=${httpError}, apmError=${apmError}`);
        onComplete();
    };

    const showMessage = (msg: DialogType) => () =>
        app
            .showModal(msg, { resource })
            .then((result) => {
                switch (result) {
                    case NADialogAction.RELOAD:
                        /* sepcReloadPage(); */
                        window.location.reload(true);
                        break;
                    case NADialogAction.RESTART:
                        /* sepcRestart(); */
                        if (customProtocolHandler) {
                            if (navigator.userAgent.indexOf('Safari')) {
                                generateOneTimeCode(customProtocolHandler, onError, false);
                            } else {
                                generateOneTimeCode(customProtocolHandler, onError, true);
                            }
                        }
                        break;
                    case NADialogAction.SHOW_DOWNLOAD_SCREEN:
                        showMessage(appt ? DialogType.APPTUN_INSTALL : DialogType.NA_INSTALL)();
                        break;
                    case NADialogAction.RESTART_NO_AUTOSTART_SUPPORTED:
                        snaDebug('***** NADialogAction.RESTART_NO_AUTOSTART_SUPPORTED *****');
                        if (customProtocolHandler) {
                            customProtocolHandler.Run(true);
                        }
                        break;
                }
            })
            .catch(() => {
                if (customProtocolHandler) {
                    customProtocolHandler.Abort();
                }
                onComplete();
            });

    customProtocolHandler = new CustomProtocolHandler({
        onAutoStartNotSupported: showMessage(
            appt ? DialogType.APPTUN_AUTO_START_NOT_SUPPORTED : DialogType.NA_AUTO_START_NOT_SUPPORTED
        ),
        onConnectionTimeout: showMessage(appt ? DialogType.APPTUN_FAILED_TO_START : DialogType.NA_FAILED_TO_START),
        onNotInstalled: showMessage(appt ? DialogType.APPTUN_INSTALL : DialogType.NA_INSTALL),
        onUserDeclined: showMessage(appt ? DialogType.APPTUN_FAILED_TO_START : DialogType.NA_FAILED_TO_START),
        onPopupBlockedIE: showMessage(appt ? DialogType.APPTUN_POPUP_BLOCKED : DialogType.NA_POPUP_BLOCKED),
        hideMessage,
        onDebugMsg: snaDebug,
        browser: userAgent.family,
        link,
        token,
        pollingEnabled: true,
        timeoutEnabled: true,
        onHandlerDetected() {
            /* */
        },
        statusCheckOptions: {
            noConnectionTimeoutVal: LAUNCH_DETECTION_TIMEOUT, // isCustomProtocolDetectionSupported() ? 4000 : 100,
            onComplete,
            onError,
            onProgress(eventName) {
                snaDebug(`NA:onProgress:${token}:${eventName}`);
                return (
                    eventName !== PROGRESS_STATUS.obtaining_status && eventName !== PROGRESS_STATUS.awaiting_connection
                );
            },
            uri: '/my.status.na',
        },
    });

    generateOneTimeCode(customProtocolHandler, onError, false);
}

function generateOneTimeCode(
    customProtocolHandler: CustomProtocolHandler,
    onError: Function,
    clickFromErrorPrompt: boolean
) {
    const xhttp = new XMLHttpRequest();
    const getTokenUrl = 'get_token_for_sessid.php3';

    xhttp.onerror = function() {
        snaDebug('NA: onerror: ' + 'GET/ get_token_for_sessid.php3 failed');
        alert('Failed to get session code. Please relaunch network access resource');
        onError();
    };

    xhttp.ontimeout = function() {
        snaDebug('NA: onerror: ' + 'request for GET/ get_token_for_sessid.php3 timed out');
        alert('Please check internet connection and relaunch network access resource');
        onError();
    };

    xhttp.onload = function() {
        const xhttpstatus = xhttp.status;
        if (xhttpstatus == 200) {
            const otcStr = '&otc=' + xhttp.getResponseHeader('X-ACCESS-Session-Token');
            customProtocolHandler.AppendOtcToLink(otcStr);
        } else {
            snaDebug('NA: httpStatusError: ' + 'GET/ get_token_for_sessid.php3 failed ' + xhttpstatus);
            alert('Failed to get session code - Http error. Please relaunch network access resource');
            onError();
            return;
        }

        if (!clickFromErrorPrompt) {
            customProtocolHandler.Run(false);

            window.addEventListener('unload', () => {
                if (customProtocolHandler) {
                    customProtocolHandler.Abort();
                }
            });
        } else {
            customProtocolHandler.Run(true);
        }
    };

    xhttp.open('GET', getTokenUrl, true);
    xhttp.timeout = 3000;
    xhttp.send();
}

function initInstallerDialog(dialog: IDialogConfiguration, components: IClientConfiguration) {
    const downloadLink = Dialog.getAction(dialog, NADialogAction.DOWNLOAD);
    if (!downloadLink) {
        return;
    }
    if (components.os !== OS.LINUX) {
        return;
    }
    const installerURLs = components.installerURLs;
    const installerField: IFormField = {
        name: INSTALLER_URL_FIELD,
        type: INPUT.RADIO,
        caption: '',
        options: installerURLs.map((entry) => ({
            text: entry.id,
            value: entry.url,
        })),
        value: installerURLs[0].url,
    };

    Dialog.insertBefore(dialog, downloadLink, installerField);
}

function getInstallerUrl(dialog: IDialogConfiguration | null, components: IClientConfiguration): string {
    if (components.os !== OS.LINUX) {
        return components.installerURL;
    }
    const installerURLs = components.installerURLs;
    if (dialog) {
        const field = Dialog.getField(dialog, INSTALLER_URL_FIELD);
        if (field) {
            return field.value as string;
        }
    }
    return installerURLs[0].url;
}

app.subscribe(EventTags.DIALOG_OPEN, ({ detail }, page) => {
    if (
        page.pageType !== PAGE.WEBTOP ||
        typeof detail.dialog !== 'string' ||
        !(detail.dialog in page.webtop.network_access.dialogs)
    ) {
        return;
    }
    const na = page.webtop.network_access;
    const dialogId = detail.dialog;
    detail.dialog = {
        ...deepCopy(na.dialogs[dialogId]),
        id: dialogId,
        class: DialogClass.NETWORK_ACCESS,
        resource: detail.resource,
    } as IDialogConfiguration;

    if (dialogId === DialogType.NA_INSTALL || dialogId === DialogType.APPTUN_INSTALL) {
        initInstallerDialog(detail.dialog, na.components);
    }
});

app.subscribe(EventTags.DIALOG_ACTION, (event, page) => {
    if (page.pageType !== PAGE.WEBTOP) {
        return;
    }
    const detail = event.detail;
    const na = page.webtop.network_access;

    if (!detail.dialog || !(detail.dialog.id in na.dialogs)) {
        return;
    }

    const dialog = Dialog.get(page, detail.dialog);
    if (detail.action === NADialogAction.DOWNLOAD) {
        window.open(getInstallerUrl(dialog, na.components), '_self');
        event.preventDefault();
    }
});

function snaGetLaunchLink(server: string, resource: IResourceModel, components: IClientConfiguration) {
    const dict = {};
    dict['server'] = server;
    dict['resourcename'] = resource.id;
    dict['resourcetype'] = resource.type;
    dict['cmd'] = 'launch';
    if (components.os === OS.WINDOWS) {
        const packageList = components.packageList;
        for (let i = 0; i < packageList.length; i++) {
            const instance = components.packages[packageList[i]];
            dict['version' + i] = instance.version;
            dict['id' + i] = instance.id
                .trim()
                .replace(/CLSID:(.*)/, '{$1}')
                .replace(/ /g, '+');
            dict['package' + i] = instance.url;
        }
    }
    const link = getCustomProtocolLink(components.scheme, dict);
    const token = hex_md5(link);
    return {
        token,
        link: link + '&token=' + token,
    };
}

export default launchNAResource;
